import { useCallback, useEffect, useRef } from 'react';
import { creditTime } from '@/core/required-videos-time';
import { nextProgress } from './use-required-video-pure';
import type { SeekFn, VideoProgress } from '@/types/required-video';

export function useFireOnce(completed: boolean, onWatched: () => void): void {
  const firedRef = useRef(false);
  useEffect(() => {
    if (completed && !firedRef.current) {
      firedRef.current = true;
      onWatched();
    }
  }, [completed, onWatched]);
}

export function useFlushOnComplete(
  completed: boolean,
  flush: () => void
): void {
  useEffect(() => {
    if (completed) flush();
  }, [completed, flush]);
}

export function useReadyOnce(progressRef: {
  current: VideoProgress;
}): (seek: SeekFn) => void {
  const seekedRef = useRef(false);
  return useCallback(
    (seek) => {
      if (seekedRef.current) return;
      seekedRef.current = true;
      const lp = progressRef.current.last_position;
      if (lp > 0) {
        try {
          seek(lp);
        } catch {
          /* seek silently failed; playback still works */
        }
      }
    },
    [progressRef]
  );
}

export function useProgressRef(progress: VideoProgress): {
  current: VideoProgress;
} {
  const ref = useRef(progress);
  useEffect(() => {
    ref.current = progress;
  }, [progress]);
  return ref;
}

type OnTimeArgs = {
  lastTRef: { current: number };
  progressRef: { current: VideoProgress };
  setProgress: (p: VideoProgress) => void;
};

export function useOnTime(a: OnTimeArgs): (t: number, d: number) => void {
  return useCallback(
    (t, d) => {
      const credit = creditTime(a.lastTRef.current, t);
      a.lastTRef.current = t;
      a.setProgress(
        nextProgress({ prev: a.progressRef.current, t, d, credit })
      );
    },
    [a]
  );
}
